import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://sibo-hub.emergent.host";

export default function Seo({
  title,
  description,
  path = "/",
  image = null,
  type = "website",
  jsonLd = null,
  keywords = null,
}) {
  const url = `${SITE}${path}`;
  const fullTitle = title ? `${title} — SIBO Institute` : "SIBO Institute — Understanding SIBO. Supporting Recovery.";
  const desc = description || "A dedicated resource for the education, support, and treatment of Small Intestinal Bacterial Overgrowth. Evidence, education, and empowerment for every step of the journey.";
  const ogImage = image || `${SITE}/og-image.jpg`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="SIBO Institute" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

// Common JSON-LD generators
export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  name: "SIBO Institute",
  url: SITE,
  logo: `${SITE}/logo.png`,
  description: "A dedicated resource for the education, support, and treatment of Small Intestinal Bacterial Overgrowth.",
  medicalSpecialty: "Gastroenterology",
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "SIBO Institute",
  url: SITE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/learn?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const articleLd = (article) => ({
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  headline: article.title,
  description: article.excerpt,
  image: article.image,
  datePublished: article.created_at || article.date,
  dateModified: article.updated_at || article.created_at || article.date,
  author: { "@type": "Organization", name: article.author || "SIBO Institute" },
  publisher: {
    "@type": "MedicalOrganization",
    name: "SIBO Institute",
    logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
  },
  mainEntityOfPage: `${SITE}/article/${article.slug}`,
  about: { "@type": "MedicalCondition", name: "Small Intestinal Bacterial Overgrowth" },
  audience: { "@type": "MedicalAudience", audienceType: "Patient" },
});

export const faqPageLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => {
    const refs = (f.references && f.references.length)
      ? `\n\nReferences: ${f.references.join("; ")}`
      : "";
    return {
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: `${f.answer}${refs}` },
    };
  }),
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE}${item.path}`,
  })),
});

export { SITE };
